import React, { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaPlay,
  FaTruck,
  FaWhatsapp,
  FaYoutube,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import {
  fetchBusinessProfileItems,
  fetchBusinessPublicProfile,
  normalizeBusinessEntityType,
} from './profileMediaApi';
import {
  buildAddressMapEmbedUrl,
  buildAddressMapPreviewUrl,
  buildAddressMapUrl,
  buildYoutubeThumbnailUrl,
} from './orderApi';
import { RESTAURANT_SERVICE_LABELS } from './restaurantServiceConstants';
import 'bootstrap/dist/css/bootstrap.min.css';
import './css/CustomerFacingProfile.css';

const SOCIAL_ICONS = {
  instagram: FaInstagram,
  facebook: FaFacebookF,
  youtube: FaYoutube,
  whatsapp: FaWhatsapp,
  twitter: FaXTwitter,
  linkedin: FaLinkedinIn,
};

function formatAddress(profile) {
  const addr =
    profile?.primaryAddress ||
    profile?.address ||
    (Array.isArray(profile?.addresses) ? profile.addresses[0] : null);
  if (!addr || typeof addr !== 'object') {
    return profile?.addressLine || profile?.location || '';
  }
  const parts = [
    addr.addressLine1 ?? addr.line1 ?? addr.AddressLine1,
    addr.addressLine2 ?? addr.line2 ?? addr.AddressLine2,
    addr.city ?? addr.City,
    addr.state ?? addr.State,
    addr.postalCode ?? addr.postal_code ?? addr.PostalCode,
  ]
    .map((p) => (p != null ? String(p).trim() : ''))
    .filter(Boolean);
  return parts.join(', ');
}

function formatHours(profile) {
  if (profile?.hoursLabel) return profile.hoursLabel;
  if (profile?.openingHoursLabel) return profile.openingHoursLabel;
  if (typeof profile?.openingHours === 'string') return profile.openingHours;
  return '';
}

function formatDeliveryOptions(profile) {
  const rows = Array.isArray(profile?.deliveryOptions) ? profile.deliveryOptions : [];
  if (rows.length) {
    return rows.map((row) => row.label || row.key).filter(Boolean).join(' · ');
  }
  if (profile?.homeDeliveryAvailable) return 'Home delivery';
  return '';
}

function normalizeServices(profile) {
  const rows = Array.isArray(profile?.services) ? profile.services : [];
  return rows
    .map((row) => {
      const key = String(row.serviceType || row.Service_Type || row.type || '').toUpperCase();
      if (!key) return null;
      const status = String(row.status || row.Status || 'ACTIVE').toUpperCase();
      if (status && status !== 'ACTIVE') return null;
      return {
        key,
        label:
          row.label ||
          RESTAURANT_SERVICE_LABELS[key] ||
          key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        minGuestCount: row.minGuestCount ?? row.Min_Guest_Count ?? null,
        maxGuestCount: row.maxGuestCount ?? row.Max_Guest_Count ?? null,
      };
    })
    .filter(Boolean);
}

/**
 * Customer-facing business profile (public page).
 */
export default function CustomerFacingProfile({ kind = 'restaurant' }) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const businessType = normalizeBusinessEntityType(
    searchParams.get('type') || params.entityType || kind,
  );
  const entityId =
    params.businessId ||
    params.entityId ||
    params.restaurantId ||
    params.storeId ||
    params.pharmaId;

  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('about');
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.documentElement.classList.add('customer-profile-public');
    document.body.style.background = '#ffffff';
    document.body.style.backgroundImage = 'none';
    return () => {
      document.documentElement.classList.remove('customer-profile-public');
      document.body.style.background = '';
      document.body.style.backgroundImage = '';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!entityId) {
        setError('Missing business id');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const [data, itemRows] = await Promise.all([
          fetchBusinessPublicProfile(businessType, entityId),
          fetchBusinessProfileItems(businessType, entityId).catch(() => []),
        ]);
        if (!cancelled) {
          setProfile(data || {});
          setItems(Array.isArray(itemRows) ? itemRows : []);
          setActiveMediaIndex(0);
          setVideoPlaying(false);
        }
      } catch (err) {
        if (!cancelled) {
          setProfile(null);
          setItems([]);
          setError(
            err?.response?.data?.message ||
              err?.response?.data?.error ||
              'Profile could not be loaded. Add media under Business Profile → Media first.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [businessType, entityId]);

  const title =
    profile?.name ||
    profile?.restaurantName ||
    profile?.storeName ||
    `${businessType} profile`;

  const logoUrl =
    profile?.logoUrl ||
    profile?.logo?.mediaUrl ||
    profile?.coverImage ||
    null;

  const photos = Array.isArray(profile?.photos)
    ? profile.photos
    : Array.isArray(profile?.kitchenPhotos)
      ? profile.kitchenPhotos
      : [];
  const videos = Array.isArray(profile?.youtubeVideos) ? profile.youtubeVideos : [];
  const socialLinks = Array.isArray(profile?.socialLinks) ? profile.socialLinks : [];
  const services = useMemo(() => normalizeServices(profile || {}), [profile]);

  const primaryAddress =
    profile?.primaryAddress ||
    profile?.address ||
    (Array.isArray(profile?.addresses) ? profile.addresses[0] : null) ||
    null;
  const addressLine = formatAddress(profile || {});
  const mapEmbedUrl = useMemo(
    () => buildAddressMapEmbedUrl(primaryAddress, addressLine),
    [primaryAddress, addressLine],
  );
  const mapExternalUrl = useMemo(
    () => buildAddressMapUrl(primaryAddress, addressLine),
    [primaryAddress, addressLine],
  );
  const mapPreviewUrl = useMemo(
    () => buildAddressMapPreviewUrl(primaryAddress, addressLine),
    [primaryAddress, addressLine],
  );

  const gallery = useMemo(() => {
    const rows = [];
    const seen = new Set();
    const pushPhoto = (photo, keyPrefix = 'p') => {
      const url = photo?.mediaUrl || photo?.url || null;
      if (!url || seen.has(url)) return;
      seen.add(url);
      rows.push({
        key: `${keyPrefix}-${photo.mediaId || url}`,
        kind: 'photo',
        mediaId: photo.mediaId,
        mediaName: photo.mediaName || photo.name || 'Photo',
        mediaUrl: url,
        thumbUrl: url,
      });
    };

    photos.forEach((photo) => pushPhoto(photo, 'p'));
    // Logo also appears in the gallery when kitchen photos are missing / as cover.
    if (logoUrl) {
      pushPhoto(
        {
          mediaId: profile?.logo?.mediaId || 'logo',
          mediaUrl: logoUrl,
          mediaName: 'Logo',
        },
        'logo',
      );
    }
    if (profile?.coverImage) {
      pushPhoto(
        {
          mediaId: 'cover',
          mediaUrl: profile.coverImage,
          mediaName: 'Cover',
        },
        'cover',
      );
    }
    videos.forEach((video) => {
      const mediaUrl = video.mediaUrl || video.url || '';
      const embedUrl = video.embedUrl || null;
      const thumbUrl =
        video.thumbUrl ||
        video.thumbnailUrl ||
        buildYoutubeThumbnailUrl(mediaUrl) ||
        buildYoutubeThumbnailUrl(embedUrl) ||
        null;
      rows.push({
        key: `v-${video.mediaId || mediaUrl}`,
        kind: 'video',
        mediaId: video.mediaId,
        mediaName: video.mediaName || video.name || 'Video',
        mediaUrl,
        embedUrl,
        thumbUrl,
      });
    });
    if (mapEmbedUrl) {
      rows.push({
        key: 'map-location',
        kind: 'map',
        mediaName: 'Location',
        embedUrl: mapEmbedUrl,
        mediaUrl: mapExternalUrl || mapEmbedUrl,
        // Static image when lat/lon exist; otherwise thumb uses Google Maps embed preview.
        thumbUrl: mapPreviewUrl || null,
      });
    }
    return rows;
  }, [
    photos,
    videos,
    logoUrl,
    profile?.logo?.mediaId,
    profile?.coverImage,
    mapEmbedUrl,
    mapExternalUrl,
    mapPreviewUrl,
  ]);

  const activeMedia = gallery[activeMediaIndex] || null;
  const aboutText = profile?.notes || profile?.about || '';
  const hoursLine = formatHours(profile || {});
  const deliveryLine = formatDeliveryOptions(profile || {});
  const contact = profile?.contactNo || profile?.phone || '';
  const hoursHeading = businessType === 'STORE' ? 'Store hours' : 'Hours';
  const tagline = String(
    profile?.tagline ||
      profile?.tagLine ||
      '',
  ).trim();
  const typeLabel =
    businessType === 'STORE'
      ? 'Store'
      : businessType === 'PHARMA'
        ? 'Pharmacy'
        : 'Restaurant';
  const metaLine = tagline || typeLabel;

  const editHref =
    businessType === 'STORE'
      ? `/store/${entityId}?tab=media`
      : businessType === 'RESTAURANT'
        ? `/restaurant/${entityId}?tab=media`
        : `/business/${entityId}/profile?type=${businessType.toLowerCase()}`;

  const visibleTabs = useMemo(() => {
    const tabs = [
      { id: 'about', label: 'About Us' },
      { id: 'products', label: 'Products' },
    ];
    if (services.length > 0) {
      tabs.push({ id: 'services', label: 'Services' });
    }
    return tabs;
  }, [services.length]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab('about');
    }
  }, [visibleTabs, activeTab]);

  const goPrev = () => {
    if (gallery.length < 2) return;
    setVideoPlaying(false);
    setActiveMediaIndex((i) => (i - 1 + gallery.length) % gallery.length);
  };

  const goNext = () => {
    if (gallery.length < 2) return;
    setVideoPlaying(false);
    setActiveMediaIndex((i) => (i + 1) % gallery.length);
  };

  const selectMedia = (index) => {
    setVideoPlaying(false);
    setActiveMediaIndex(index);
  };

  return (
    <div className="biz-profile">
      <Container className="biz-profile__container">
        {loading ? <p className="biz-profile__status">Loading profile…</p> : null}
        {error ? (
          <div className="biz-profile__error-wrap">
            <p className="biz-profile__error">{error}</p>
            <Link to={editHref} className="biz-profile__edit-link">
              Edit media
            </Link>
          </div>
        ) : null}

        {!loading && !error && profile ? (
          <div className="biz-profile__sheet">
            <Row className="g-4 align-items-start">
              <Col xs={12} md={12} lg={7} xl={8} className="order-1">
                <section className="biz-profile__media" aria-label="Photos and videos">
                  <div className="biz-profile__gallery">
                    <div className="biz-profile__stage">
                      {!activeMedia ? (
                        <div className="biz-profile__stage-empty">No media yet</div>
                      ) : activeMedia.kind === 'map' && activeMedia.embedUrl ? (
                        <iframe
                          title="Business location map"
                          src={activeMedia.embedUrl}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          allowFullScreen
                        />
                      ) : activeMedia.kind === 'video' &&
                        videoPlaying &&
                        activeMedia.embedUrl ? (
                        <iframe
                          title={activeMedia.mediaName || 'Video'}
                          src={`${activeMedia.embedUrl}${
                            activeMedia.embedUrl.includes('?') ? '&' : '?'
                          }autoplay=1`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : activeMedia.kind === 'video' ? (
                        <button
                          type="button"
                          className="biz-profile__stage-preview"
                          onClick={() => setVideoPlaying(true)}
                          aria-label="Play video"
                        >
                          {activeMedia.thumbUrl ? (
                            <img
                              src={activeMedia.thumbUrl}
                              alt={activeMedia.mediaName || 'Video preview'}
                            />
                          ) : (
                            <span className="biz-profile__stage-empty">Video</span>
                          )}
                          <span className="biz-profile__play-orb">
                            <FaPlay aria-hidden="true" />
                          </span>
                        </button>
                      ) : (
                        <img src={activeMedia.mediaUrl} alt={activeMedia.mediaName || title} />
                      )}

                      {gallery.length > 1 ? (
                        <>
                          <button
                            type="button"
                            className="biz-profile__nav biz-profile__nav--prev"
                            onClick={goPrev}
                            aria-label="Previous media"
                          >
                            <FaChevronLeft aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="biz-profile__nav biz-profile__nav--next"
                            onClick={goNext}
                            aria-label="Next media"
                          >
                            <FaChevronRight aria-hidden="true" />
                          </button>
                        </>
                      ) : null}

                      {gallery.length > 0 ? (
                        <span className="biz-profile__stage-count">
                          {activeMediaIndex + 1} / {gallery.length}
                        </span>
                      ) : null}
                    </div>

                    {gallery.length > 0 ? (
                      <div className="biz-profile__thumbs" role="list">
                        {gallery.map((item, index) => (
                          <button
                            key={item.key}
                            type="button"
                            role="listitem"
                            className={`biz-profile__thumb${
                              item.kind === 'map' ? ' biz-profile__thumb--map' : ''
                            }${index === activeMediaIndex ? ' is-active' : ''}`}
                            onClick={() => selectMedia(index)}
                            aria-label={
                              item.kind === 'map'
                                ? 'Location map'
                                : item.kind === 'video'
                                  ? 'Video'
                                  : `Photo ${index + 1}`
                            }
                          >
                            {item.kind === 'map' ? (
                              item.thumbUrl ? (
                                <img src={item.thumbUrl} alt="" />
                              ) : item.embedUrl ? (
                                <span className="biz-profile__thumb-map-preview" aria-hidden="true">
                                  <iframe
                                    className="biz-profile__thumb-map-frame"
                                    src={item.embedUrl}
                                    title=""
                                    loading="lazy"
                                    tabIndex={-1}
                                    referrerPolicy="no-referrer-when-downgrade"
                                  />
                                </span>
                              ) : (
                                <span className="biz-profile__thumb-fallback" />
                              )
                            ) : item.thumbUrl ? (
                              <img src={item.thumbUrl} alt="" />
                            ) : (
                              <span className="biz-profile__thumb-fallback" />
                            )}
                            {item.kind === 'video' ? (
                              <span className="biz-profile__thumb-play">
                                <FaPlay aria-hidden="true" />
                              </span>
                            ) : null}
                            {item.kind === 'map' ? (
                              <span className="biz-profile__thumb-map-icon">
                                <FaMapMarkerAlt aria-hidden="true" />
                              </span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </section>
              </Col>

              <Col xs={12} md={12} lg={5} xl={4} className="order-2">
                <aside className="biz-profile__details">
                  <div className="biz-profile__title-row">
                    {logoUrl ? (
                      <img className="biz-profile__logo" src={logoUrl} alt="" />
                    ) : null}
                    <div className="biz-profile__title-text">
                      <h1>{title}</h1>
                      {metaLine ? <p className="biz-profile__meta">{metaLine}</p> : null}
                    </div>
                  </div>

                  <p className="biz-profile__section-label">Details</p>

                  <ul className="biz-profile__info-list">
                    <li>
                      <FaMapMarkerAlt aria-hidden="true" />
                      <div>
                        <strong>Address</strong>
                        <span>{addressLine || 'Not added yet'}</span>
                      </div>
                    </li>
                    <li>
                      <FaClock aria-hidden="true" />
                      <div>
                        <strong>{hoursHeading}</strong>
                        <span>{hoursLine || 'Hours not set yet'}</span>
                      </div>
                    </li>
                    <li>
                      <FaTruck aria-hidden="true" />
                      <div>
                        <strong>Delivery options</strong>
                        <span>{deliveryLine || 'Not configured yet'}</span>
                      </div>
                    </li>
                    <li>
                      <FaPhoneAlt aria-hidden="true" />
                      <div>
                        <strong>Contact</strong>
                        <span>{contact || 'Not added yet'}</span>
                      </div>
                    </li>
                  </ul>

                  {aboutText ? (
                    <p className="biz-profile__blurb">
                      {aboutText.length > 180 ? `${aboutText.slice(0, 180).trim()}…` : aboutText}
                    </p>
                  ) : (
                    <p className="biz-profile__blurb biz-profile__blurb--muted">
                      Add a short description in Details notes.
                    </p>
                  )}

                  {socialLinks.length > 0 ? (
                    <div className="biz-profile__follow">
                      <p className="biz-profile__section-label">Follow us on</p>
                      <div className="biz-profile__social">
                        {socialLinks.map((link) => {
                          const Icon = SOCIAL_ICONS[link.platform] || FaInstagram;
                          return (
                            <a
                              key={link.platform}
                              className="biz-profile__social-btn"
                              href={link.url}
                              target="_blank"
                              rel="noreferrer"
                              title={link.label || link.platform}
                              aria-label={link.label || link.platform}
                            >
                              <Icon aria-hidden="true" />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </aside>
              </Col>

              <Col xs={12} className="order-3">
                <section className="biz-profile__tabs-section">
                  <div className="biz-profile__tabs" role="tablist" aria-label="Profile sections">
                    {visibleTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`biz-profile__tab${activeTab === tab.id ? ' is-active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="biz-profile__tab-panel" role="tabpanel">
                    {activeTab === 'about' ? (
                      <div className="biz-profile__about-panel">
                        {aboutText ? (
                          <p>{aboutText}</p>
                        ) : (
                          <p className="biz-profile__empty">
                            No about text yet. Add notes on the business Details tab.
                          </p>
                        )}
                      </div>
                    ) : null}

                    {activeTab === 'products' ? (
                      items.length === 0 ? (
                        <p className="biz-profile__empty">No products to show yet.</p>
                      ) : (
                        <Row className="g-3">
                          {items.map((item) => (
                            <Col key={item.id || item.name} xs={6} sm={4} md={3} lg={2}>
                              <article className="biz-profile__item">
                                <div className="biz-profile__item-media">
                                  {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <span />}
                                </div>
                                <h3>{item.name}</h3>
                                {item.price != null && item.price !== '' ? <p>₹{item.price}</p> : null}
                              </article>
                            </Col>
                          ))}
                        </Row>
                      )
                    ) : null}

                    {activeTab === 'services' ? (
                      services.length === 0 ? (
                        <p className="biz-profile__empty">No services enabled yet.</p>
                      ) : (
                        <Row className="g-3">
                          {services.map((service) => (
                            <Col key={service.key} xs={12} sm={6} md={4} lg={3}>
                              <article className="biz-profile__service-card">
                                <h3>{service.label}</h3>
                                {service.minGuestCount != null || service.maxGuestCount != null ? (
                                  <p>
                                    Guests:{' '}
                                    {service.minGuestCount != null ? service.minGuestCount : '—'}
                                    {' – '}
                                    {service.maxGuestCount != null ? service.maxGuestCount : '—'}
                                  </p>
                                ) : (
                                  <p>Available</p>
                                )}
                              </article>
                            </Col>
                          ))}
                        </Row>
                      )
                    ) : null}
                  </div>
                </section>
              </Col>
            </Row>
          </div>
        ) : null}
      </Container>
    </div>
  );
}
